import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const LifestyleEstimationSidebar = ({ onAddTemplate }) => {
  const [selectedCity, setSelectedCity] = useState('new-york');
  const [selectedLifestyle, setSelectedLifestyle] = useState('moderate');

  const cityOptions = [
    { value: 'new-york', label: 'New York, NY' },
    { value: 'san-francisco', label: 'San Francisco, CA' },
    { value: 'los-angeles', label: 'Los Angeles, CA' },
    { value: 'chicago', label: 'Chicago, IL' },
    { value: 'miami', label: 'Miami, FL' },
    { value: 'seattle', label: 'Seattle, WA' },
    { value: 'boston', label: 'Boston, MA' },
    { value: 'austin', label: 'Austin, TX' }
  ];

  const lifestyleOptions = [
    { value: 'basic', label: 'Basic Lifestyle' },
    { value: 'moderate', label: 'Moderate Lifestyle' },
    { value: 'comfortable', label: 'Comfortable Lifestyle' },
    { value: 'luxury', label: 'Luxury Lifestyle' }
  ];

  const expenseTemplates = [
    {
      category: 'medical',
      title: 'Healthcare Essentials',
      items: [
        { description: 'Health Insurance Premium', amount: 450, frequency: 'monthly' },
        { description: 'Annual Health Checkup', amount: 800, frequency: 'annually' },
        { description: 'Dental Care', amount: 150, frequency: 'quarterly' },
        { description: 'Vision Care', amount: 200, frequency: 'annually' }
      ]
    },
    {
      category: 'home',
      title: 'Home & Utilities',
      items: [
        { description: 'Rent/Mortgage', amount: 2500, frequency: 'monthly' },
        { description: 'Utilities (Electric, Gas, Water)', amount: 180, frequency: 'monthly' },
        { description: 'Internet & Cable', amount: 120, frequency: 'monthly' },
        { description: 'Home Maintenance', amount: 200, frequency: 'monthly' }
      ]
    },
    {
      category: 'food',
      title: 'Food & Dining',
      items: [
        { description: 'Groceries', amount: 600, frequency: 'monthly' },
        { description: 'Dining Out', amount: 400, frequency: 'monthly' },
        { description: 'Coffee & Snacks', amount: 150, frequency: 'monthly' }
      ]
    },
    {
      category: 'transport',
      title: 'Transportation',
      items: [
        { description: 'Car Payment', amount: 450, frequency: 'monthly' },
        { description: 'Auto Insurance', amount: 120, frequency: 'monthly' },
        { description: 'Gas & Fuel', amount: 200, frequency: 'monthly' },
        { description: 'Car Maintenance', amount: 100, frequency: 'monthly' }
      ]
    }
  ];

  const getCityMultiplier = (city) => {
    const multipliers = {
      'new-york': 1.4,
      'san-francisco': 1.5,
      'los-angeles': 1.2,
      'chicago': 1.0,
      'miami': 1.1,
      'seattle': 1.2,
      'boston': 1.3,
      'austin': 0.9
    };
    return multipliers[city] || 1.0;
  };

  const getLifestyleMultiplier = (lifestyle) => {
    const multipliers = {
      'basic': 0.7,
      'moderate': 1.0,
      'comfortable': 1.4,
      'luxury': 2.0
    };
    return multipliers[lifestyle] || 1.0;
  };

  const handleAddTemplate = (template) => {
    const cityMultiplier = getCityMultiplier(selectedCity);
    const lifestyleMultiplier = getLifestyleMultiplier(selectedLifestyle);
    const totalMultiplier = cityMultiplier * lifestyleMultiplier;

    template.items.forEach(item => {
      const adjustedAmount = Math.round(item.amount * totalMultiplier);
      onAddTemplate({
        description: item.description,
        amount: adjustedAmount,
        category: template.category,
        frequency: item.frequency,
        startYear: new Date().getFullYear(),
        endYear: new Date().getFullYear() + 25,
        inflationRate: 3.5,
        probability: 100,
        isOneTime: false
      });
    });
  };

  return (
    <div className="w-80 bg-card border-l border-border h-full overflow-y-auto">
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4">Lifestyle Estimation</h3>
        
        <div className="space-y-4">
          <Select
            label="Metro City"
            options={cityOptions}
            value={selectedCity}
            onChange={setSelectedCity}
          />
          
          <Select
            label="Lifestyle Level"
            options={lifestyleOptions}
            value={selectedLifestyle}
            onChange={setSelectedLifestyle}
          />
        </div>

        <div className="mt-4 p-3 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">Cost Adjustment</p>
          <p className="text-lg font-semibold text-foreground">
            {((getCityMultiplier(selectedCity) * getLifestyleMultiplier(selectedLifestyle) - 1) * 100).toFixed(0)}%
          </p>
          <p className="text-xs text-muted-foreground">vs. baseline Chicago moderate</p>
        </div>
      </div>

      <div className="p-6">
        <h4 className="font-medium text-foreground mb-4">Quick Add Templates</h4>
        
        <div className="space-y-4">
          {expenseTemplates.map((template, index) => (
            <div key={index} className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h5 className="font-medium text-foreground">{template.title}</h5>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleAddTemplate(template)}
                >
                  <Icon name="Plus" size={16} className="mr-1" />
                  Add All
                </Button>
              </div>
              
              <div className="space-y-2">
                {template.items.map((item, itemIndex) => {
                  const adjustedAmount = Math.round(
                    item.amount * getCityMultiplier(selectedCity) * getLifestyleMultiplier(selectedLifestyle)
                  );
                  
                  return (
                    <div key={itemIndex} className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{item.description}</span>
                      <span className="font-medium text-foreground">
                        ${adjustedAmount}/{item.frequency === 'monthly' ? 'mo' : item.frequency === 'annually' ? 'yr' : 'qtr'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-accent/10 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Lightbulb" size={20} className="text-accent mt-0.5" />
            <div>
              <h5 className="font-medium text-foreground mb-1">Smart Suggestions</h5>
              <p className="text-sm text-muted-foreground">
                Templates are adjusted for your selected city and lifestyle. You can modify amounts after adding.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LifestyleEstimationSidebar;